import React, { useState, useEffect } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../components/ui/table";
import { 
  Search, 
  MapPin, 
  Building2, 
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Trash2,
  Zap
} from "lucide-react";

const TRADE_CATEGORIES = [
  "Plumber",
  "Electrician",
  "Painter",
  "Carpenter",
  "Roofer",
  "Builder",
  "Handyman",
  "Landscaper",
  "Locksmith",
  "Plasterer",
  "Tiler",
  "Window Cleaner",
  "Flooring Specialist",
  "HVAC Technician",
  "Gas Engineer",
  "Bricklayer",
  "Decorator",
  "Kitchen Fitter",
  "Bathroom Fitter",
  "Gardener",
];

const RADIUS_OPTIONS = [
  { value: "5", label: "5 km" },
  { value: "10", label: "10 km" },
  { value: "25", label: "25 km" },
  { value: "50", label: "50 km" },
  { value: "100", label: "100 km" },
];

export const OutscraperSearch = ({ onSearchComplete }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  
  const [formData, setFormData] = useState({
    category: "",
    customCategory: "",
    location: "",
    radius_km: "10",
    limit: "50",
  });

  // Check if there's an active search in progress
  const activeJob = jobs.find(job => job.status === 'pending' || job.status === 'processing' || job.status === 'retrying');

  useEffect(() => {
    fetchJobs();
  }, []);

  // Auto-refresh jobs list while there's an active search
  useEffect(() => {
    if (activeJob) {
      const interval = setInterval(() => {
        fetchJobs();
      }, 2000); // Refresh every 2 seconds while active
      return () => clearInterval(interval);
    }
  }, [activeJob?.id]);

  const fetchJobs = async () => {
    setLoadingJobs(true);
    try {
      const response = await apiClient.get("/outscraper/jobs");
      setJobs(response.data);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleSearch = async () => {
    const category = formData.category === "custom" ? formData.customCategory : formData.category;
    
    if (!category || !formData.location) {
      toast.error("Please fill in category and location");
      return;
    }

    setSearching(true);
    try {
      const response = await apiClient.post("/outscraper/search", {
        category: category,
        location: formData.location,
        radius_km: parseInt(formData.radius_km),
        limit: parseInt(formData.limit),
        emails_only: true,
      });
      
      // Immediately add the new job to the UI (optimistic update)
      const newJob = {
        id: response.data.job_id,
        category: category,
        location: formData.location,
        status: 'pending',
        contacts_added: 0,
        emails_found: 0,
        created_at: new Date().toISOString()
      };
      setJobs(prevJobs => [newJob, ...prevJobs]);
      
      toast.success("Search started! Results will appear shortly.");
      setDialogOpen(false);
      
      // Start polling immediately
      pollJobStatus(response.data.job_id);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const pollJobStatus = async (jobId) => {
    const checkStatus = async () => {
      try {
        const response = await apiClient.get(`/outscraper/jobs/${jobId}`);
        const job = response.data;
        
        // Update the job in the list
        setJobs(prevJobs => prevJobs.map(j => j.id === jobId ? job : j));
        
        if (job.status === "completed") {
          const noEmailMsg = job.no_email_count > 0 ? ` (${job.no_email_count} without email)` : '';
          toast.success(`Search complete! Added ${job.contacts_added} contacts${noEmailMsg}`);
          fetchJobs(); // Final refresh to get accurate data
          if (onSearchComplete) onSearchComplete();
          return;
        } else if (job.status === "failed") {
          toast.error(`Search failed: ${job.error || "Unknown error"}`);
          fetchJobs();
          return;
        }
        
        // Still processing, check again in 2 seconds
        setTimeout(checkStatus, 2000);
      } catch (error) {
        console.error("Error checking job status:", error);
        // Retry on error
        setTimeout(checkStatus, 3000);
      }
    };
    
    // Start checking immediately
    checkStatus();
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await apiClient.delete(`/outscraper/jobs/${jobId}`);
      toast.success("Job deleted");
      fetchJobs();
    } catch (error) {
      toast.error("Failed to delete job");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: Clock, color: "bg-slate-100 text-slate-700", label: "Pending" },
      processing: { icon: Loader2, color: "bg-blue-100 text-blue-700", label: "Searching..." },
      retrying: { icon: RefreshCw, color: "bg-amber-100 text-amber-700", label: "Retrying..." },
      completed: { icon: CheckCircle, color: "bg-emerald-100 text-emerald-700", label: "Completed" },
      failed: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Failed" },
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
        <badge.icon className={`w-3 h-3 ${(status === 'processing' || status === 'retrying') ? 'animate-spin' : ''}`} />
        {badge.label}
      </span>
    );
  };

  return (
    <>
      <Card className="border-slate-200 bg-gradient-to-br from-orange-50 to-white">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Zap className="w-5 h-5 text-orange-600" />
            Find Businesses
          </CardTitle>
          <CardDescription>
            Search for tradespeople by category and location using Outscraper
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Active Search Indicator */}
          {activeJob && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg animate-pulse">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-blue-900">Search in Progress</p>
                  <p className="text-sm text-blue-700">
                    Searching for <span className="font-semibold">{activeJob.category}</span> in <span className="font-semibold">{activeJob.location}</span>
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    {activeJob.status === 'retrying' ? 'Retrying for more email results...' : 'This may take a minute...'}
                  </p>
                </div>
                {getStatusBadge(activeJob.status)}
              </div>
            </div>
          )}

          <Button
            onClick={() => setDialogOpen(true)}
            className="w-full bg-orange-600 hover:bg-orange-700"
            data-testid="outscraper-search-btn"
            disabled={!!activeJob}
          >
            {activeJob ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Search in Progress...
              </>
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                Search for Businesses
              </>
            )}
          </Button>
          
          {/* Recent Jobs */}
          {jobs.length > 0 && (
            <div className="mt-4">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Recent Searches</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {jobs.slice(0, 5).map((job) => (
                  <div 
                    key={job.id} 
                    className={`flex items-center justify-between p-2 bg-white rounded border text-sm ${
                      job.status === 'processing' || job.status === 'pending' || job.status === 'retrying'
                        ? 'border-blue-300 bg-blue-50/50'
                        : 'border-slate-200'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">
                        {job.category} in {job.location}
                      </p>
                      <p className="text-xs text-slate-500">
                        {job.status === 'processing' || job.status === 'pending' || job.status === 'retrying' ? (
                          <span className="text-blue-600">Searching...</span>
                        ) : (
                          <>
                            {job.contacts_added} contacts added
                            {job.emails_found > 0 && (
                              <span className="text-emerald-600 font-medium"> ({job.emails_found} with email)</span>
                            )}
                            {job.no_email_count > 0 && !job.emails_found && ` (${job.no_email_count} without email)`}
                            {job.retry_performed && (
                              <span className="text-amber-600 ml-1">(retried)</span>
                            )}
                          </>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      {getStatusBadge(job.status)}
                      {job.status !== 'processing' && job.status !== 'pending' && job.status !== 'retrying' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteJob(job.id)}
                          className="text-slate-400 hover:text-red-600 h-7 w-7 p-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Search className="w-5 h-5" />
              Search for Businesses
            </DialogTitle>
            <DialogDescription>
              Find tradespeople in your target area. All results will be saved - use the email filter toggle in Contacts to show only those with emails.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Category */}
            <div>
              <Label htmlFor="category">Business Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="mt-1.5" data-testid="category-select">
                  <SelectValue placeholder="Select a trade category" />
                </SelectTrigger>
                <SelectContent>
                  {TRADE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                  <SelectItem value="custom">Custom category...</SelectItem>
                </SelectContent>
              </Select>
              
              {formData.category === "custom" && (
                <Input
                  placeholder="Enter custom category"
                  value={formData.customCategory}
                  onChange={(e) => setFormData({ ...formData, customCategory: e.target.value })}
                  className="mt-2"
                  data-testid="custom-category-input"
                />
              )}
            </div>

            {/* Location */}
            <div>
              <Label htmlFor="location">Location (Town/City or Postcode)</Label>
              <div className="relative mt-1.5">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="location"
                  placeholder="e.g., London, Manchester, SW1A 1AA"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="pl-10"
                  data-testid="location-input"
                />
              </div>
            </div>

            {/* Radius */}
            <div>
              <Label>Search Radius</Label>
              <Select
                value={formData.radius_km}
                onValueChange={(value) => setFormData({ ...formData, radius_km: value })}
              >
                <SelectTrigger className="mt-1.5" data-testid="radius-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RADIUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Limit */}
            <div>
              <Label htmlFor="limit">Maximum Results</Label>
              <Input
                id="limit"
                type="number"
                min="10"
                max="500"
                value={formData.limit}
                onChange={(e) => setFormData({ ...formData, limit: e.target.value })}
                className="mt-1.5"
                data-testid="limit-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                All results will be saved. Use the email filter in Contacts to view only those with emails.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSearch}
              disabled={searching}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="start-search-btn"
            >
              {searching ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Starting...
                </span>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Start Search
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
